(() => {
  const formRootSelector = '[data-forms-id="forms-root-1075510"]';
  const injectedStyleId = 'mobimat-contact-responsive';
  const desktopMaxWidth = '700px';
  const requiredFieldSelectors = [
    '#first_name',
    '#email',
    'input[type="tel"]',
    'input[id="custom#country"]',
    'input[id="custom#state_region"]',
    'input[id="custom#subject"]',
    'textarea[name="custom#your_message"]',
  ];
  const enhancedShadowRoots = new WeakSet();
  const prefilledShadowRoots = new WeakSet();
  const quoteParams = new URLSearchParams(window.location.search);
  const isQuoteRequest = quoteParams.get('quote') === 'true';
  const responsiveStyles = `
    .mobimat-required-indicator {
      margin-left: 2px;
    }

    @media (min-width: 750px) {
      :host {
        --container-max-w: 700px;
        --inline-container-max-width: 700px;
      }

      form[data-testid='form'] {
        display: grid !important;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        column-gap: 14px !important;
        row-gap: 12px !important;
        width: 100% !important;
        max-width: none !important;
        box-sizing: border-box !important;
      }

      form[data-testid='form'] > div:first-child,
      form[data-testid='form'] > div:has(input[id='custom#subject']),
      form[data-testid='form'] > div:has(textarea),
      form[data-testid='form'] > div:has(input[type='file']),
      form[data-testid='form'] > button[data-testid='btn-form-submit'] {
        grid-column: 1 / -1;
      }

      form[data-testid='form'] textarea {
        min-height: 180px !important;
      }

      form[data-testid='form'] > button[data-testid='btn-form-submit'] {
        justify-self: start !important;
        width: auto !important;
        min-width: 130px !important;
        padding-inline: 30px !important;
      }
    }
  `;

  function getRequiredFields(shadowRoot) {
    return requiredFieldSelectors
      .map((selector) => shadowRoot.querySelector(selector))
      .filter(Boolean);
  }

  function prepareRequiredFields(shadowRoot) {
    const form = shadowRoot.querySelector('form[data-testid="form"]');
    const requiredFields = getRequiredFields(shadowRoot);

    if (form) form.noValidate = false;

    requiredFields.forEach((field) => {
      const label = field.labels?.[0] || field.parentElement?.querySelector('label');

      if (field.matches('textarea') && !field.id) {
        field.id = 'custom#your_message';
      }

      field.required = true;
      field.setAttribute('aria-required', 'true');
      field.dataset.mobimatRequiredField = 'true';

      if (field.id === 'email') field.type = 'email';

      if (label) {
        if (label.id) field.setAttribute('aria-labelledby', label.id);

        if (!label.querySelector('.mobimat-required-indicator')) {
          const indicator = document.createElement('span');
          indicator.className = 'mobimat-required-indicator';
          indicator.setAttribute('aria-hidden', 'true');
          indicator.textContent = '*';
          label.append(indicator);
        }
      }
    });

    return requiredFields;
  }

  function updateFieldValidity(field) {
    field.setCustomValidity('');

    if (!field.value.trim()) {
      field.setCustomValidity('Please complete this required field.');
    } else if (field.id === 'email' && field.validity.typeMismatch) {
      field.setCustomValidity('Please enter a valid email address.');
    } else if (field.type === 'tel' && field.value.replace(/\D/g, '').length < 7) {
      field.setCustomValidity('Please enter a valid phone number.');
    }

    return field.checkValidity();
  }

  function validateRequiredFields(shadowRoot) {
    const requiredFields = prepareRequiredFields(shadowRoot);
    const firstInvalidField = requiredFields.find((field) => !updateFieldValidity(field));

    if (!firstInvalidField) return true;

    firstInvalidField.focus();
    firstInvalidField.reportValidity();
    return false;
  }

  function installRequiredFieldValidation(shadowRoot) {
    prepareRequiredFields(shadowRoot);

    if (enhancedShadowRoots.has(shadowRoot)) return;
    enhancedShadowRoots.add(shadowRoot);

    shadowRoot.addEventListener('input', (event) => {
      if (event.target?.matches?.('[data-mobimat-required-field="true"]')) {
        event.target.setCustomValidity('');
      }
    });

    shadowRoot.addEventListener(
      'blur',
      (event) => {
        if (event.target?.matches?.('[data-mobimat-required-field="true"]')) {
          updateFieldValidity(event.target);
        }
      },
      true
    );

    shadowRoot.addEventListener(
      'click',
      (event) => {
        if (!event.target?.closest?.('[data-testid="btn-form-submit"]')) return;
        if (validateRequiredFields(shadowRoot)) return;

        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );

    shadowRoot.addEventListener(
      'submit',
      (event) => {
        if (validateRequiredFields(shadowRoot)) return;

        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );
  }

  function updateShopifyFormField(field, value) {
    const fieldPrototype = field.matches('textarea') ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
    const valueSetter = Object.getOwnPropertyDescriptor(fieldPrototype, 'value')?.set;

    if (valueSetter) {
      valueSetter.call(field, value);
    } else {
      field.value = value;
    }

    field.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    field.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  function prefillQuoteRequest(shadowRoot) {
    if (!isQuoteRequest || prefilledShadowRoots.has(shadowRoot)) return true;

    const subjectField = shadowRoot.querySelector('input[id="custom#subject"]');
    const messageField = shadowRoot.querySelector('textarea[name="custom#your_message"]');

    if (!subjectField || !messageField) return false;

    const product = quoteParams.get('product') || '';
    const variant = quoteParams.get('variant') || '';
    const sku = quoteParams.get('sku') || '';
    const messageLines = ['Quote Request', ''];

    if (product) messageLines.push(`Product: ${product}`);
    if (variant) messageLines.push(`Variant: ${variant}`);
    if (sku) messageLines.push(`SKU: ${sku}`);

    messageLines.push(
      '',
      'Quantity needed: [enter quantity]',
      'Delivery address for shipping estimate: [enter your address]',
      '',
      'I would like to receive a quote including pricing and shipping costs. Please contact me with a full estimate.'
    );

    updateShopifyFormField(subjectField, 'Quote Request');
    updateShopifyFormField(messageField, messageLines.join('\n'));
    prefilledShadowRoots.add(shadowRoot);

    return true;
  }

  function enhanceContactForm() {
    const formRoots = document.querySelectorAll(formRootSelector);

    if (formRoots.length === 0) return false;

    let allFormsReady = true;

    formRoots.forEach((formRoot) => {
      const appBlock = formRoot.closest('.shopify-app-block');
      const embed = formRoot.querySelector('shopify-forms-embed');

      if (appBlock) {
        appBlock.style.width = '100%';
        appBlock.style.maxWidth = desktopMaxWidth;
      }

      formRoot.style.width = '100%';

      if (!embed) {
        allFormsReady = false;
        return;
      }

      embed.style.width = '100%';

      if (!embed.shadowRoot) {
        allFormsReady = false;
        return;
      }

      if (!embed.shadowRoot.getElementById(injectedStyleId)) {
        const style = document.createElement('style');
        style.id = injectedStyleId;
        style.textContent = responsiveStyles;
        embed.shadowRoot.append(style);
      }

      installRequiredFieldValidation(embed.shadowRoot);

      if (!prefillQuoteRequest(embed.shadowRoot)) {
        allFormsReady = false;
      }
    });

    return allFormsReady;
  }

  function enhanceWithRetry(attempt = 0) {
    if (enhanceContactForm() || attempt >= 60) return;
    window.setTimeout(() => enhanceWithRetry(attempt + 1), 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => enhanceWithRetry(), { once: true });
  } else {
    enhanceWithRetry();
  }

  document.addEventListener('shopify:section:load', () => enhanceWithRetry());
})();
